import React from 'react';
import {
  Box,
  Button,
  Badge,
  Divider,
  Typography,
  Skeleton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { COLORS } from '../constants';
import SearchField from '../../templates/SearchField';

const styles = {
  // Mobile
  mobileContainer: { display: 'flex', flexDirection: 'column', px: 3, pt: 2, pb: 1, gap: 1.5 },
  mobileTypeSelector: { display: 'flex', flexDirection: 'column', gap: 0.25 },
  mobileTypeSelectorRow: { display: 'flex', alignItems: 'center' },
  mobileTypeButton: (taskTypeName) => ({
    textTransform: 'none',
    fontWeight: taskTypeName ? 700 : 400,
    fontSize: '1rem',
    color: taskTypeName ? 'text.primary' : 'text.secondary',
    p: 0,
    minWidth: 0,
    overflow: 'hidden',
    '& .MuiButton-endIcon': { flexShrink: 0 },
    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
  }),
  mobileTypeButtonLabel: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mobileCounterRow: { display: 'flex', alignItems: 'baseline', gap: 0.5 },
  mobileButtonsRow: { display: 'flex', gap: 1, pb: 0.5, alignItems: 'center' },
  mobileFilterButtonWrap: { flex: 1, position: 'relative' },
  mobileFilterBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    bgcolor: 'error.main',
    color: '#fff',
    borderRadius: '50%',
    width: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.65rem',
    fontWeight: 700,
    pointerEvents: 'none',
  },

  // Desktop
  desktopBreadcrumb: { display: 'flex', alignItems: 'center', px: 3, pt: 2, pb: 0.5 },
  desktopBreadcrumbSeparator: { mx: 0.75 },
  desktopTypeButton: (taskTypeName) => ({
    textTransform: 'none',
    fontWeight: taskTypeName ? 600 : 400,
    color: taskTypeName ? 'text.primary' : 'text.secondary',
    p: 0,
    minWidth: 0,
    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
  }),
  desktopStoreName: { ml: 'auto', pl: 2, fontSize: '0.7rem' },
  desktopToolbar: { display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5 },
  desktopCounterRow: { display: 'flex', alignItems: 'baseline', gap: 0.5, mr: 'auto', flexShrink: 0 },
  desktopSearch: { width: 260 },
  excelButton: {
    bgcolor: COLORS.EXCEL,
    borderColor: COLORS.EXCEL,
    color: '#fff',
    '&:hover': { bgcolor: COLORS.EXCEL_HOVER, borderColor: COLORS.EXCEL_HOVER },
    py: '5px',
  },
  excelButtonDesktop: {
    px: 2,
    bgcolor: COLORS.EXCEL,
    borderColor: COLORS.EXCEL,
    color: '#fff',
    '&:hover': { bgcolor: COLORS.EXCEL_HOVER, borderColor: COLORS.EXCEL_HOVER },
  },
  buttonIcon: { fontSize: 16 },
  mobileFilterButton: { py: '5px' },
  desktopFilterButton: { px: 2 },
};

export default function TasksHeader({
  isMobile,
  currentTaskType,
  taskTypeName,
  activeCount,
  isLoading,
  showAsCompleted,
  counterValue,
  counterTotal,
  search,
  onSearch,
  onFiltersOpen,
  onTaskTypeOpen,
  onExcelOpen,
  currentStoreName,
  currentChain,
  excelTaskTypes,
  t,
}) {
  const showExcel = excelTaskTypes.includes(currentTaskType);
  // counterTotal only arrives while narrowing filters are active, turning the
  // counter into filtered/total without touching the label next to it.
  const counterDisplay = counterTotal != null ? `${counterValue}/${counterTotal}` : counterValue;

  const renderSearchField = (extraSx = {}) => (
    <SearchField
      value={search}
      onChange={onSearch}
      placeholder={t('bruno.tasks.search', 'Search')}
      sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' }, ...extraSx }}
    />
  );

  if (isMobile) {
    return (
      <Box sx={styles.mobileContainer}>
        {/* Type selector + Stats */}
        <Box sx={styles.mobileTypeSelector}>
          <Box sx={styles.mobileTypeSelectorRow}>
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={onTaskTypeOpen}
              sx={styles.mobileTypeButton(taskTypeName)}
            >
              <Box component="span" sx={styles.mobileTypeButtonLabel}>
                {taskTypeName || t('bruno.camera.select_type', 'Select task type')}
              </Box>
            </Button>
          </Box>

          {!currentTaskType ? null : (
            <Box sx={styles.mobileCounterRow}>
              {isLoading ? (
                <Skeleton variant="text" width={60} height={16} />
              ) : showAsCompleted ? (
                <>
                  <Typography variant="caption" fontWeight={700} lineHeight={1} color="success.main">
                    {counterDisplay}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('bruno.home.completed', 'Completed')}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="caption" fontWeight={700} lineHeight={1} color="primary.main">
                    {counterDisplay}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('bruno.home.pending_tasks', 'Pending')}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Search */}
        {renderSearchField({ width: '100%' })}

        {/* Buttons */}
        <Box sx={styles.mobileButtonsRow}>
          {showExcel && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ImportExportIcon sx={styles.buttonIcon} />}
              onClick={onExcelOpen}
              sx={styles.excelButton}
            >
              {t('bruno.tasks.export_excel', 'Excel')}
            </Button>
          )}
          <Box sx={styles.mobileFilterButtonWrap}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              startIcon={<FilterListIcon />}
              onClick={onFiltersOpen}
              sx={styles.mobileFilterButton}
            >
              {t('bruno.buttons.filters', 'Filters')}
            </Button>
            {activeCount > 0 && (
              <Box sx={styles.mobileFilterBadge}>
                {activeCount}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Desktop
  return (
    <>
      {/* Breadcrumb */}
      <Box sx={styles.desktopBreadcrumb}>
        <Typography variant="body2" color="text.secondary">
          {t('bruno.drawer.tasks', 'Tasks')}
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={styles.desktopBreadcrumbSeparator}>
          /
        </Typography>
        <Button
          size="small"
          endIcon={<KeyboardArrowDownIcon fontSize="small" />}
          onClick={onTaskTypeOpen}
          sx={styles.desktopTypeButton(taskTypeName)}
        >
          {taskTypeName || t('bruno.camera.select_type', 'Select task type')}
        </Button>
        {currentStoreName && (
          <Typography variant="caption" color="text.disabled" noWrap sx={styles.desktopStoreName}>
            {currentChain ? `${currentChain} · ` : ''}
            {currentStoreName.split(' - ').slice(1).join(' - ') || currentStoreName}
          </Typography>
        )}
      </Box>

      {/* Stats + search + filters */}
      <Box sx={styles.desktopToolbar}>
        <Box sx={styles.desktopCounterRow}>
          {!currentTaskType ? null : isLoading ? (
            <Skeleton variant="text" width={120} height={28} />
          ) : showAsCompleted ? (
            <>
              <Typography variant="h6" fontWeight={700} lineHeight={1} color="success.main">
                {counterDisplay}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('bruno.home.completed', 'Completed')}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" fontWeight={700} lineHeight={1}>
                {counterDisplay}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('bruno.home.pending_tasks', 'pending')}
              </Typography>
            </>
          )}
        </Box>

        {renderSearchField(styles.desktopSearch)}

        {showExcel && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ImportExportIcon sx={styles.buttonIcon} />}
            onClick={onExcelOpen}
            sx={styles.excelButtonDesktop}
          >
            {t('bruno.tasks.export_excel', 'Excel')}
          </Button>
        )}

        <Badge badgeContent={activeCount} color="error" overlap="circular">
          <Button variant="contained" size="small" startIcon={<FilterListIcon />} onClick={onFiltersOpen} sx={styles.desktopFilterButton}>
            {t('bruno.buttons.filters', 'Filters')}
          </Button>
        </Badge>
      </Box>

      <Divider />
    </>
  );
}
